// PRNT-4313: the placeholder/loader gating for an unpublished event guest site.
//
// Token-bearing visitors must wait for GetMe to resolve before deciding whether
// to render the "Site Under Construction" placeholder; otherwise admins would
// see it flash briefly before their identity is known.

import type { DeriveUnpublishedSiteGateInput, DeriveUnpublishedSiteGateResult, UnpublishedGateOptions, UnpublishedGatePreviewVisibility } from './GuestSite.types';

// Assemble the gate input; pure, extracted so the canPreviewDraft-never-hasUnlocked wiring is unit-testable. See MCP_PREVIEW.md#draft-gate.
export const buildUnpublishedGateInput = (previewVisibility: UnpublishedGatePreviewVisibility, options: UnpublishedGateOptions): DeriveUnpublishedSiteGateInput => ({
  hasIdentifiedUserOnce: options.hasIdentifiedUserOnce,
  hadTokenAtMount: options.hadTokenAtMount,
  isAdmin: options.isAdmin,
  isDraftViewAuthorized: previewVisibility.canPreviewDraft,
  isDraftViewAuthorizationPending: previewVisibility.isDraftViewAuthorizationPending,
  isGuestSiteSsrEnabled: options.isGuestSiteSsrEnabled,
  isPreviewing: options.isPreviewing,
  isWebsitePublishCapabilityEnabled: options.isWebsitePublishCapabilityEnabled,
  isWebsitePublished: options.isWebsitePublished,
});

export const deriveUnpublishedSiteGate = (input: DeriveUnpublishedSiteGateInput): DeriveUnpublishedSiteGateResult => {
  const {
    hasIdentifiedUserOnce,
    hadTokenAtMount,
    isAdmin,
    isDraftViewAuthorized,
    isDraftViewAuthorizationPending,
    isGuestSiteSsrEnabled,
    isPreviewing,
    isWebsitePublishCapabilityEnabled,
    isWebsitePublished,
  } = input;

  // Only gate when capability is on, not previewing, and the site is unpublished.
  const isUnpublishedGateActive = !isPreviewing && isWebsitePublishCapabilityEnabled && !isWebsitePublished;

  // Anonymous SSR visitor (no token) can decide immediately. Token-bearing
  // visitors must wait for hasIdentifiedUserOnce so isAdmin is decisive.
  const authResolvedForUnpublishedGate = hasIdentifiedUserOnce || (isGuestSiteSsrEnabled && !hadTokenAtMount);

  // Placeholder shows only for a non-admin, non-draft-authorized viewer; hold (don't deny) while the grant resolves — see MCP_PREVIEW.md#draft-gate.
  const showUnpublishedPage = !isDraftViewAuthorizationPending && authResolvedForUnpublishedGate && isUnpublishedGateActive && !isAdmin && !isDraftViewAuthorized;
  const suppressGuestSiteUntilAuth = isUnpublishedGateActive && ((hadTokenAtMount && !hasIdentifiedUserOnce) || isDraftViewAuthorizationPending);

  return { showUnpublishedPage, suppressGuestSiteUntilAuth };
};
